import type { CSSProperties, ReactNode, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useState } from 'react';

// ─────────────────────────────────────────────────────────────
// Field — form primitives consuming CSS-var design tokens.
// Used by redesigned forms (e.g. Change Password).
// ─────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function Field({ label, required, error, hint, children, className, style }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }} className={className}>
      <label
        style={{
          fontSize: 13.5,
          fontWeight: 500,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        {label}
        {required && (
          <span style={{ color: 'var(--color-bad)', fontSize: 11, marginTop: -2 }}>*</span>
        )}
      </label>
      {children}
      {error ? (
        <div style={{ fontSize: 11.5, color: 'var(--color-bad)', lineHeight: 1.3 }}>{error}</div>
      ) : hint ? (
        <div style={{ fontSize: 11.5, color: 'var(--color-text-dim)', lineHeight: 1.3 }}>{hint}</div>
      ) : null}
    </div>
  );
}

const baseControl: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 13.5,
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
  background: 'var(--color-panel)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  outline: 'none',
  transition: 'border-color 120ms, box-shadow 120ms',
};

function applyFocusStyle(el: HTMLElement, error?: boolean) {
  el.style.borderColor = error ? 'var(--color-bad)' : 'var(--color-accent-bright)';
  el.style.boxShadow = error
    ? '0 0 0 3px rgba(185,28,28,0.12)'
    : '0 0 0 3px rgba(111,191,74,0.18)';
}
function clearFocusStyle(el: HTMLElement, error?: boolean) {
  el.style.borderColor = error ? 'var(--color-bad)' : 'var(--color-border)';
  el.style.boxShadow = 'none';
}

interface CommonInputProps {
  error?: boolean;
}

export function TextInput({
  error,
  style,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & CommonInputProps) {
  return (
    <input
      {...props}
      style={{
        ...baseControl,
        borderColor: error ? 'var(--color-bad)' : 'var(--color-border)',
        ...style,
      }}
      onFocus={(e) => { applyFocusStyle(e.currentTarget, error); props.onFocus?.(e); }}
      onBlur={(e) => { clearFocusStyle(e.currentTarget, error); props.onBlur?.(e); }}
    />
  );
}

export function SelectInput({
  error,
  style,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & CommonInputProps) {
  return (
    <select
      {...props}
      style={{
        ...baseControl,
        paddingRight: 36,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        borderColor: error ? 'var(--color-bad)' : 'var(--color-border)',
        cursor: 'pointer',
        background:
          `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 12px center`,
        ...style,
      }}
      onFocus={(e) => { applyFocusStyle(e.currentTarget, error); props.onFocus?.(e); }}
      onBlur={(e) => { clearFocusStyle(e.currentTarget, error); props.onBlur?.(e); }}
    >
      {children}
    </select>
  );
}

export function TextareaInput({
  error,
  style,
  rows = 4,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & CommonInputProps) {
  return (
    <textarea
      {...props}
      rows={rows}
      style={{
        ...baseControl,
        height: 'auto',
        padding: '10px 12px',
        lineHeight: 1.5,
        resize: 'vertical',
        borderColor: error ? 'var(--color-bad)' : 'var(--color-border)',
        ...style,
      }}
      onFocus={(e) => { applyFocusStyle(e.currentTarget, error); props.onFocus?.(e); }}
      onBlur={(e) => { clearFocusStyle(e.currentTarget, error); props.onBlur?.(e); }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Switch — on/off toggle (used for Active / Inactive status etc.)
// ─────────────────────────────────────────────────────────────

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
}

export function Switch({ checked, onChange, label, description }: SwitchProps) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onChange(!checked);
          }
        }}
        tabIndex={0}
        style={{
          position: 'relative',
          display: 'inline-block',
          width: 36,
          height: 20,
          borderRadius: 10,
          background: checked ? 'var(--color-good)' : 'var(--color-border)',
          transition: 'background 180ms',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: 8,
            background: '#fff',
            boxShadow: '0 1px 3px rgba(15,23,32,0.18)',
            transition: 'left 180ms',
          }}
        />
      </span>
      {(label || description) && (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          {label && (
            <span style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>{label}</span>
          )}
          {description && (
            <span style={{ fontSize: 11.5, color: 'var(--color-text-dim)' }}>{description}</span>
          )}
        </span>
      )}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// FormSection — labelled grouping of fields inside a form
// ─────────────────────────────────────────────────────────────

interface FormSectionProps {
  number?: number;
  title: string;
  description?: string;
  children: ReactNode;
  highlight?: boolean;
}

export function FormSection({ number, title, description, children, highlight }: FormSectionProps) {
  const headerNode = (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 1.4,
          color: highlight ? 'var(--color-accent)' : 'var(--color-text-dim)',
          fontFamily: 'var(--font-mono)',
          fontWeight: 500,
        }}
      >
        {number !== undefined && (
          <span style={{ color: 'var(--color-accent)' }}>
            {String(number).padStart(2, '0')}
          </span>
        )}
        <span>{title}</span>
      </div>
      {description && (
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)', lineHeight: 1.45 }}>
          {description}
        </div>
      )}
    </header>
  );

  if (highlight) {
    return (
      <section
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: '16px 18px 18px',
          background: 'rgba(87,154,59,0.04)',
          border: '1px solid rgba(87,154,59,0.18)',
          borderLeft: '3px solid var(--color-accent)',
          borderRadius: 10,
        }}
      >
        {headerNode}
        {children}
      </section>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {headerNode}
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Button variants for forms
// ─────────────────────────────────────────────────────────────

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        background:
          disabled
            ? 'var(--color-border)'
            : hover
              ? 'var(--color-accent-hover)'
              : 'var(--color-accent)',
        color: disabled ? 'var(--color-text-dim)' : '#fff',
        border: 'none',
        padding: '10px 18px',
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        color: 'var(--color-text-mid)',
        border: '1px solid var(--color-border)',
        padding: '10px 18px',
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}
